// Shared show/hide controller for the v2 (work_model) location details UI
// (ILJ-814/815).
//
// Both the post wizard's "Location details" step
// (frontend/components/post_a_job/wizard/post_wizard.ts) and the employer
// job ad edit form (app/views/employer/job_ads/_form.html.slim) render the
// same set of conditional blocks — office attendance, visa/relocation
// criteria, and the remote question tree — driven by the work_model radios.
// This class is the single source of truth for that behaviour so it can't
// drift between the two surfaces.
//
// The work-model radio group container id differs between surfaces (the
// wizard's `work-model-options-radio` vs the edit form's
// `job-work-model-options`), so it is injected via the constructor. The EOR
// advisory block only exists on the wizard, so it is treated as optional
// throughout.
class LocationDetailsV2 {
  workModelContainerId: string;

  constructor(workModelContainerId: string) {
    this.workModelContainerId = workModelContainerId;
  }

  hideBlock(block: HTMLElement | null): void {
    if (!block) return;

    block.style['display'] = 'none';
    // The relocation criteria blocks are siblings of this block on the edit form,
    // so re-run the toggle to hide them when the relocation section is hidden.
    if (block.id === 'work-permit-and-relocation-block') this.handleRelocationOptionsToggleV2();
  }

  // These are required answers, so once one is picked the empty placeholder must
  // not be selectable again — the user can only switch to another option, never
  // back to blank (Daniela QA, ILJ-1407). Disabling the placeholder option keeps
  // it visible as the initial empty state while blocking re-selection. No-ops on
  // the edit form, whose Yes/No answers are radios, not selects.
  lockPlaceholders(): void {
    [this.workModelContainerId, 'open-to-worldwide-radio', 'timezone-preference-radio'].forEach(
      containerId => {
        const select = document.getElementById(containerId)?.querySelector('select');
        const placeholder = select?.querySelector('option[value=""]') as HTMLOptionElement | null;
        if (placeholder) placeholder.disabled = true;
      }
    );
  }

  // Clears every dependent answer when the work model changes (Daniela QA,
  // ILJ-1407) so the new branch starts blank. Native selects go back to their
  // placeholder (selectedIndex 0 works even where the placeholder is disabled),
  // the international checkboxes and criteria clear, and the React location fields
  // clear via the location-details:reset event.
  resetDependentFields(): void {
    const nativeSelectIds = [
      'job_ad_form_open_to_worldwide',
      'job_ad_form_timezone_preference',
      'job_ad_form_office_attendance_count',
      'job_ad_form_office_attendance_period',
      'job_ad_form_eligible_timezone_from',
      'job_ad_form_eligible_timezone_to',
    ];
    nativeSelectIds.forEach(id => {
      const select = document.getElementById(id) as HTMLSelectElement | null;
      if (select) select.selectedIndex = 0;
    });

    ['job_ad_form_citizenship', 'job_ad_form_visa_support', 'job_ad_form_relocation_paid'].forEach(
      id => {
        const checkbox = document.getElementById(id) as HTMLInputElement | null;
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = false;
        }
      }
    );
    document.querySelectorAll('#work-permit-and-relocation-block textarea').forEach(textarea => {
      (textarea as HTMLTextAreaElement).value = '';
    });

    document.dispatchEvent(new Event('location-details:reset'));
    this.handleRelocationOptionsToggleV2();
  }

  init(): void {
    const locationBlock = document.getElementById('location-block') as HTMLElement;
    const workPermitRelocationBlock = document.getElementById(
      'work-permit-and-relocation-block'
    ) as HTMLElement;
    const officeAttendanceBlock = document.getElementById('office-attendance-block') as HTMLElement;
    const eorAdvisoryBlock = document.getElementById('eor-advisory-block') as HTMLElement;

    const setEorAdvisoryVisible = (visible: boolean) => {
      if (eorAdvisoryBlock) {
        eorAdvisoryBlock.style['display'] = visible ? 'block' : 'none';
        if (visible) document.dispatchEvent(new Event('eor-advisory:shown'));
      }
    };

    // Apply initial state based on current work_model selection
    const applyWorkModelState = () => {
      const workModel = this.workModelValue();

      if (workModel === 'onsite' || workModel === 'hybrid') {
        this.hideRemoteFields();
        this.showOnsiteHybridFields(locationBlock, workPermitRelocationBlock, officeAttendanceBlock);
      } else if (workModel === 'remote') {
        this.hideBlock(locationBlock);
        this.hideBlock(officeAttendanceBlock);
        this.showRemoteFields();
      } else {
        this.hideBlock(locationBlock);
        this.hideBlock(workPermitRelocationBlock);
        this.hideBlock(officeAttendanceBlock);
        this.hideRemoteFields();
      }

      // EOR advisory is always visible now (ILJ-1405), independent of the
      // selected work model.
      setEorAdvisoryVisible(true);
    };

    applyWorkModelState();
    this.lockPlaceholders();

    const radioGroup = document.getElementById(this.workModelContainerId);
    if (radioGroup) {
      // applyWorkModelState already keeps the EOR advisory visible, so there is
      // no need to toggle it again here (avoids a duplicate eor-advisory:shown).
      radioGroup.onchange = () => {
        // Changing work model is a top-level switch, so clear every dependent
        // answer and the new branch starts blank (Daniela QA, ILJ-1407). Only the
        // wizard (a <select>) resets; the edit form's radios keep their behaviour.
        if (radioGroup.querySelector('select')) this.resetDependentFields();
        applyWorkModelState();
      };
    }

    $(document).on('change', '#work-permit-and-relocation-block', () => {
      return this.handleRelocationOptionsToggleV2();
    });

    $(document).on('change', '#open-to-worldwide-radio', () => {
      this.applyOpenToWorldwideState();
    });

    $(document).on('change', '#timezone-preference-radio', () => {
      this.applyTimezonePreferenceState();
    });

    $(() => {
      const relocationError = $('#work-permit-and-relocation-block .ld-error-input').length > 0;
      relocationError && $('#work-permit-and-relocation-block').show();
      this.handleRelocationOptionsToggleV2();
    });
  }

  // Reads a container's current value from either the wizard's <select>
  // (ILJ-1404 work model, ILJ-1407 Yes/No answers) or the edit form's radio
  // buttons. Returns null when nothing is chosen — the empty placeholder option
  // or no checked radio — which the state machine treats as "no selection".
  valueFromContainer(containerId: string): string | null {
    const container = document.getElementById(containerId);
    if (!container) return null;

    const select = container.querySelector('select') as HTMLSelectElement | null;
    if (select) return select.value || null;

    const radio = container.querySelector('input[type="radio"]:checked') as HTMLInputElement | null;
    return radio ? radio.value : null;
  }

  workModelValue(): string | null {
    return this.valueFromContainer(this.workModelContainerId);
  }

  showOnsiteHybridFields(
    locationBlock: HTMLElement | null,
    workPermitRelocationBlock: HTMLElement | null,
    officeAttendanceBlock: HTMLElement | null
  ): void {
    if (locationBlock) locationBlock.style['display'] = 'block';

    // International candidates and Eligible locations are shown as soon as Onsite/Hybrid
    // is selected, independently of whether an office has been added yet (ILJ-814 AC).
    if (workPermitRelocationBlock) workPermitRelocationBlock.style['display'] = 'block';

    const eligibleLocationsBlock = document.getElementById('eligible-locations-block') as HTMLElement;
    if (eligibleLocationsBlock) eligibleLocationsBlock.style['display'] = 'block';

    // Office attendance only shown for hybrid
    if (this.workModelValue() === 'hybrid') {
      if (officeAttendanceBlock) officeAttendanceBlock.style['display'] = 'block';
    } else {
      this.hideBlock(officeAttendanceBlock);
    }
  }

  // Remote branch (ILJ-815) — question tree cascading visibility.
  showRemoteFields(): void {
    const openToWorldwideBlock = document.getElementById('open-to-worldwide-block') as HTMLElement;
    if (openToWorldwideBlock) openToWorldwideBlock.style['display'] = 'block';

    this.applyOpenToWorldwideState();
  }

  hideRemoteFields(): void {
    const blockIds = [
      'open-to-worldwide-block',
      'eligible-locations-block',
      'timezone-preference-block',
      'eligible-timezones-block',
      'excluded-locations-block',
    ];

    blockIds.forEach(id => {
      this.hideBlock(document.getElementById(id) as HTMLElement);
    });
  }

  applyOpenToWorldwideState(): void {
    const eligibleLocationsBlock = document.getElementById('eligible-locations-block') as HTMLElement;
    const timezonePreferenceBlock = document.getElementById('timezone-preference-block') as HTMLElement;
    const workPermitRelocationBlock = document.getElementById(
      'work-permit-and-relocation-block'
    ) as HTMLElement;
    const worldwideValue = this.valueFromContainer('open-to-worldwide-radio');

    if (!worldwideValue) {
      this.hideBlock(eligibleLocationsBlock);
      this.hideBlock(timezonePreferenceBlock);
      this.hideBlock(workPermitRelocationBlock);
      this.hideTimezonePreferenceFields();
      return;
    }

    const openToWorldwide = worldwideValue === 'true';

    if (openToWorldwide) {
      this.hideBlock(eligibleLocationsBlock);
      if (timezonePreferenceBlock) timezonePreferenceBlock.style['display'] = 'block';
      this.hideBlock(workPermitRelocationBlock);
      this.applyTimezonePreferenceState();
    } else {
      if (eligibleLocationsBlock) eligibleLocationsBlock.style['display'] = 'block';
      this.hideBlock(timezonePreferenceBlock);
      // Remote jobs have no office locations, so international candidates fields
      // must be shown based on open_to_worldwide alone, not on getLocationCount().
      if (workPermitRelocationBlock) workPermitRelocationBlock.style['display'] = 'block';
      this.hideTimezonePreferenceFields();
    }
  }

  applyTimezonePreferenceState(): void {
    const eligibleTimezonesBlock = document.getElementById('eligible-timezones-block') as HTMLElement;
    const excludedLocationsBlock = document.getElementById('excluded-locations-block') as HTMLElement;
    const timezonePreference = this.valueFromContainer('timezone-preference-radio');

    // Excluded locations only appears once the timezone-preference question is
    // answered (either Yes or No), not as soon as the worldwide branch opens
    // (ILJ-1352 progressive disclosure).
    if (timezonePreference) {
      if (excludedLocationsBlock) excludedLocationsBlock.style['display'] = 'block';
    } else {
      this.hideBlock(excludedLocationsBlock);
    }

    if (timezonePreference === 'true') {
      if (eligibleTimezonesBlock) eligibleTimezonesBlock.style['display'] = 'block';
    } else {
      this.hideBlock(eligibleTimezonesBlock);
    }
  }

  hideTimezonePreferenceFields(): void {
    this.hideBlock(document.getElementById('eligible-timezones-block') as HTMLElement);
    this.hideBlock(document.getElementById('excluded-locations-block') as HTMLElement);
  }

  handleRelocationOptionsToggleV2(): void {
    // When the relocation section itself is hidden (e.g. Remote + open to worldwide),
    // keep its criteria blocks hidden too. On the employer edit form they are rendered
    // as siblings of #work-permit-and-relocation-block, so hiding the parent does not
    // hide them through the DOM tree; this also stops them showing on init (Copilot).
    if ($('#work-permit-and-relocation-block').is(':hidden')) {
      $('#visa-support-criteria-block, #relocation-criteria-block, #support-criteria').hide();
      return;
    }

    const visaSupport = $('#job_ad_form_visa_support');
    const citizenship = $('#job_ad_form_citizenship');
    const relocationSupport = $('#job_ad_form_relocation_paid');

    citizenship.prop('disabled', visaSupport.is(':checked') && !citizenship.is(':checked'));
    visaSupport.prop('disabled', citizenship.is(':checked') && !visaSupport.is(':checked'));

    if (visaSupport.is(':checked')) {
      $('#visa-support-criteria-block').show();
    } else {
      $('#visa-support-criteria-block').hide();
    }

    if (relocationSupport.is(':checked')) {
      $('#relocation-criteria-block').show();
    } else {
      $('#relocation-criteria-block').hide();
    }

    // Keep legacy support-criteria block hidden in v2 (replaced by individual criteria fields)
    $('#support-criteria').hide();
  }
}

export default LocationDetailsV2;
